const MARKETING_ROUTE = "/menu/marketing";

const informativeStep = (selector, statusText, extra = {}) => ({
  type: "wait",
  selector,
  allowManualSkip: true,
  skipLabel: "academy_marketing_workflow_understood",
  statusText,
  ...extra,
});

const scenarioStep = (scenario, step) => ({
  ensure: {
    selector: '[data-academy="marketing-campaign-drawer"]',
    click: '[data-academy="marketing-create"] .vcrm-btn.primary',
  },
  dispatch: "vcrm:academy-marketing-scenario",
  detail: { scenario, step },
});

export const MARKETING_GLOBAL_WORKFLOW = {
  key: "marketing_global_campaign",
  title: "academy_marketing_global_workflow_title",
  subtitle: "academy_marketing_global_workflow_subtitle",
  description: "academy_marketing_global_workflow_description",
  estimatedMinutes: 8,
  pathLabel: "academy_marketing_global_workflow_path",
  introActionLabel: "academy_marketing_workflow_start",
  introFootnote: "academy_marketing_workflow_safety_note",
  cleanup: { dispatch: "vcrm:academy-marketing-cleanup" },
  steps: [
    {
      id: "intro",
      title: "academy_marketing_global_intro_title",
      body: "academy_marketing_global_intro_body",
      route: MARKETING_ROUTE,
      autoNavigate: true,
      checklist: [
        "academy_marketing_global_intro_check_1",
        "academy_marketing_global_intro_check_2",
        "academy_marketing_global_intro_check_3",
      ],
    },
    {
      id: "open-wizard",
      title: "academy_marketing_open_wizard_title",
      body: "academy_marketing_open_wizard_body",
      route: MARKETING_ROUTE,
      autoNavigate: true,
      targetHint: "academy_marketing_open_wizard_hint",
      interaction: {
        type: "click",
        selector: '[data-academy="marketing-create"] .vcrm-btn.primary',
        statusText: "academy_marketing_open_wizard_status",
      },
    },
    {
      id: "wizard-map",
      title: "academy_marketing_wizard_map_title",
      body: "academy_marketing_wizard_map_body",
      prepare: scenarioStep("global", 0),
      interaction: informativeStep(
        '[data-academy="marketing-wizard-navigation"]',
        "academy_marketing_wizard_map_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-wizard-navigation"]',
            '[data-academy="marketing-wizard-footer"]',
          ],
        },
      ),
    },
    {
      id: "global-basics",
      title: "academy_marketing_global_basics_title",
      body: "academy_marketing_global_basics_body",
      prepare: scenarioStep("global", 0),
      interaction: informativeStep(
        '[data-academy="marketing-campaign-title"]',
        "academy_marketing_global_basics_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-campaign-title"]',
            '[data-academy="marketing-campaign-type"]',
            '[data-academy="marketing-campaign-channel"]',
            '[data-academy="marketing-campaign-trigger"]',
          ],
        },
      ),
    },
    {
      id: "global-dates",
      title: "academy_marketing_global_dates_title",
      body: "academy_marketing_global_dates_body",
      prepare: scenarioStep("global", 0),
      interaction: informativeStep(
        '[data-academy="marketing-delivery-date"]',
        "academy_marketing_global_dates_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-delivery-date"]',
            '[data-academy="marketing-start-date"]',
            '[data-academy="marketing-end-date"]',
            '[data-academy="marketing-short-description"]',
          ],
        },
      ),
    },
    {
      id: "fixed-preview",
      title: "academy_marketing_fixed_preview_title",
      body: "academy_marketing_fixed_preview_body",
      interaction: informativeStep(
        '[data-academy="marketing-fixed-preview"]',
        "academy_marketing_fixed_preview_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-fixed-preview"]',
            '[data-academy="marketing-preview-device-switch"]',
            '[data-academy="marketing-preview-fullscreen"]',
          ],
        },
      ),
    },
    {
      id: "open-fullscreen",
      title: "academy_marketing_open_fullscreen_title",
      body: "academy_marketing_open_fullscreen_body",
      targetHint: "academy_marketing_open_fullscreen_hint",
      interaction: {
        type: "click",
        selector: '[data-academy="marketing-preview-fullscreen"]',
        statusText: "academy_marketing_open_fullscreen_status",
      },
    },
    {
      id: "fullscreen-tools",
      title: "academy_marketing_fullscreen_tools_title",
      body: "academy_marketing_fullscreen_tools_body",
      interaction: informativeStep(
        '[data-academy="marketing-fullscreen-preview"]',
        "academy_marketing_fullscreen_tools_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-fullscreen-modes"]',
            '[data-academy="marketing-fullscreen-device-switch"]',
            '[data-academy="marketing-fullscreen-preview-close"]',
          ],
        },
      ),
    },
    {
      id: "message-heading",
      title: "academy_marketing_message_heading_title",
      body: "academy_marketing_message_heading_body",
      prepare: {
        click: '[data-academy="marketing-fullscreen-preview-close"]',
        ...scenarioStep("global", 1),
      },
      interaction: informativeStep(
        '[data-academy="marketing-email-subject"]',
        "academy_marketing_message_heading_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-email-subject"]',
            '[data-academy="marketing-email-preheader"]',
          ],
        },
      ),
    },
    {
      id: "html-editor",
      title: "academy_marketing_html_editor_title",
      body: "academy_marketing_html_editor_body",
      prepare: scenarioStep("global", 1),
      interaction: informativeStep(
        '[data-academy="marketing-email-editor"]',
        "academy_marketing_html_editor_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-email-editor-actions"]',
            '[data-academy="marketing-email-variables"]',
            '[data-academy="marketing-email-html-code"]',
          ],
        },
      ),
    },
    {
      id: "telegram-copy",
      title: "academy_marketing_telegram_copy_title",
      body: "academy_marketing_telegram_copy_body",
      prepare: scenarioStep("global", 1),
      interaction: informativeStep(
        '[data-academy="marketing-telegram-text"]',
        "academy_marketing_telegram_copy_status",
      ),
    },
    {
      id: "global-audience",
      title: "academy_marketing_global_audience_title",
      body: "academy_marketing_global_audience_body",
      prepare: scenarioStep("global", 2),
      interaction: informativeStep(
        '[data-academy="marketing-audience-readiness"]',
        "academy_marketing_global_audience_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-audience-readiness"]',
            '[data-academy="marketing-global-audience-note"]',
          ],
        },
      ),
    },
    {
      id: "global-lifecycle",
      title: "academy_marketing_global_lifecycle_title",
      body: "academy_marketing_global_lifecycle_body",
      prepare: scenarioStep("global", 3),
      interaction: informativeStep(
        '[data-academy="marketing-wizard-conditions"]',
        "academy_marketing_global_lifecycle_status",
      ),
    },
    {
      id: "global-coupon",
      title: "academy_marketing_coupon_title",
      body: "academy_marketing_coupon_body",
      prepare: scenarioStep("global", 3),
      interaction: informativeStep(
        '[data-academy="marketing-coupon-settings"]',
        "academy_marketing_coupon_status",
      ),
    },
    {
      id: "test-delivery",
      title: "academy_marketing_test_title",
      body: "academy_marketing_test_body",
      prepare: scenarioStep("global", 3),
      interaction: informativeStep(
        '[data-academy="marketing-test-delivery"]',
        "academy_marketing_test_status",
      ),
    },
    {
      id: "review",
      title: "academy_marketing_review_title",
      body: "academy_marketing_review_body",
      prepare: scenarioStep("global", 4),
      interaction: informativeStep(
        '[data-academy="marketing-wizard-review"]',
        "academy_marketing_review_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-wizard-review"]',
            '[data-academy="marketing-review-edit-actions"]',
            '[data-academy="marketing-wizard-create"]',
          ],
        },
      ),
    },
    {
      id: "create-safely",
      title: "academy_marketing_global_create_title",
      body: "academy_marketing_global_create_body",
      prepare: scenarioStep("global", 4),
      targetHint: "academy_marketing_create_hint",
      interaction: informativeStep(
        '[data-academy="marketing-wizard-create"]',
        "academy_marketing_create_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-wizard-create"]',
            '[data-academy="marketing-wizard-cancel"]',
          ],
        },
      ),
    },
    {
      id: "complete",
      title: "academy_marketing_global_complete_title",
      body: "academy_marketing_global_complete_body",
      checklist: [
        "academy_marketing_global_complete_check_1",
        "academy_marketing_global_complete_check_2",
        "academy_marketing_global_complete_check_3",
        "academy_marketing_global_complete_check_4",
      ],
    },
  ],
};

export const MARKETING_BIRTHDAY_WORKFLOW = {
  key: "marketing_birthday_campaign",
  title: "academy_marketing_birthday_workflow_title",
  subtitle: "academy_marketing_birthday_workflow_subtitle",
  description: "academy_marketing_birthday_workflow_description",
  estimatedMinutes: 9,
  pathLabel: "academy_marketing_birthday_workflow_path",
  introActionLabel: "academy_marketing_workflow_start",
  introFootnote: "academy_marketing_workflow_safety_note",
  cleanup: { dispatch: "vcrm:academy-marketing-cleanup" },
  steps: [
    {
      id: "intro",
      title: "academy_marketing_birthday_intro_title",
      body: "academy_marketing_birthday_intro_body",
      route: MARKETING_ROUTE,
      autoNavigate: true,
      checklist: [
        "academy_marketing_birthday_intro_check_1",
        "academy_marketing_birthday_intro_check_2",
        "academy_marketing_birthday_intro_check_3",
      ],
    },
    {
      id: "open-wizard",
      title: "academy_marketing_open_wizard_title",
      body: "academy_marketing_open_wizard_body",
      route: MARKETING_ROUTE,
      autoNavigate: true,
      targetHint: "academy_marketing_open_wizard_hint",
      interaction: {
        type: "click",
        selector: '[data-academy="marketing-create"] .vcrm-btn.primary',
        statusText: "academy_marketing_open_wizard_status",
      },
    },
    {
      id: "birthday-mode",
      title: "academy_marketing_birthday_mode_title",
      body: "academy_marketing_birthday_mode_body",
      prepare: scenarioStep("birthday", 0),
      interaction: informativeStep(
        '[data-academy="marketing-birthday-rule"]',
        "academy_marketing_birthday_mode_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-campaign-type"]',
            '[data-academy="marketing-campaign-trigger"]',
            '[data-academy="marketing-birthday-rule"]',
          ],
        },
      ),
    },
    {
      id: "birthday-dates",
      title: "academy_marketing_birthday_dates_title",
      body: "academy_marketing_birthday_dates_body",
      prepare: scenarioStep("birthday", 0),
      interaction: informativeStep(
        '[data-academy="marketing-birthday-delivery-date"]',
        "academy_marketing_birthday_dates_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-birthday-delivery-date"]',
            '[data-academy="marketing-birthday-start-date"]',
            '[data-academy="marketing-birthday-validity"]',
          ],
        },
      ),
    },
    {
      id: "birthday-uniqueness",
      title: "academy_marketing_birthday_uniqueness_title",
      body: "academy_marketing_birthday_uniqueness_body",
      prepare: scenarioStep("birthday", 0),
      interaction: informativeStep(
        '[data-academy="marketing-birthday-rule"]',
        "academy_marketing_birthday_uniqueness_status",
      ),
    },
    {
      id: "fixed-preview",
      title: "academy_marketing_fixed_preview_title",
      body: "academy_marketing_birthday_preview_body",
      interaction: informativeStep(
        '[data-academy="marketing-fixed-preview"]',
        "academy_marketing_fixed_preview_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-fixed-preview"]',
            '[data-academy="marketing-preview-fullscreen"]',
          ],
        },
      ),
    },
    {
      id: "open-fullscreen",
      title: "academy_marketing_open_fullscreen_title",
      body: "academy_marketing_open_fullscreen_body",
      targetHint: "academy_marketing_open_fullscreen_hint",
      interaction: {
        type: "click",
        selector: '[data-academy="marketing-preview-fullscreen"]',
        statusText: "academy_marketing_open_fullscreen_status",
      },
    },
    {
      id: "fullscreen-tools",
      title: "academy_marketing_fullscreen_tools_title",
      body: "academy_marketing_fullscreen_tools_body",
      interaction: informativeStep(
        '[data-academy="marketing-fullscreen-preview"]',
        "academy_marketing_fullscreen_tools_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-fullscreen-modes"]',
            '[data-academy="marketing-fullscreen-device-switch"]',
            '[data-academy="marketing-fullscreen-preview-close"]',
          ],
        },
      ),
    },
    {
      id: "birthday-subject",
      title: "academy_marketing_birthday_subject_title",
      body: "academy_marketing_birthday_subject_body",
      prepare: {
        click: '[data-academy="marketing-fullscreen-preview-close"]',
        ...scenarioStep("birthday", 1),
      },
      interaction: informativeStep(
        '[data-academy="marketing-email-subject"]',
        "academy_marketing_birthday_subject_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-email-subject"]',
            '[data-academy="marketing-email-preheader"]',
          ],
        },
      ),
    },
    {
      id: "birthday-template",
      title: "academy_marketing_birthday_template_title",
      body: "academy_marketing_birthday_template_body",
      prepare: scenarioStep("birthday", 1),
      interaction: informativeStep(
        '[data-academy="marketing-email-editor"]',
        "academy_marketing_birthday_template_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-email-editor-actions"]',
            '[data-academy="marketing-email-variables"]',
            '[data-academy="marketing-email-html-code"]',
          ],
        },
      ),
    },
    {
      id: "birthday-telegram",
      title: "academy_marketing_telegram_copy_title",
      body: "academy_marketing_birthday_telegram_body",
      prepare: scenarioStep("birthday", 1),
      interaction: informativeStep(
        '[data-academy="marketing-telegram-text"]',
        "academy_marketing_telegram_copy_status",
      ),
    },
    {
      id: "birthday-audience",
      title: "academy_marketing_birthday_audience_title",
      body: "academy_marketing_birthday_audience_body",
      prepare: scenarioStep("birthday", 2),
      interaction: informativeStep(
        '[data-academy="marketing-audience-readiness"]',
        "academy_marketing_birthday_audience_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-audience-readiness"]',
            '[data-academy="marketing-birthday-audience-note"]',
          ],
        },
      ),
    },
    {
      id: "telegram-connection",
      title: "academy_marketing_telegram_connection_title",
      body: "academy_marketing_telegram_connection_body",
      prepare: scenarioStep("birthday", 2),
      interaction: informativeStep(
        '[data-academy="marketing-audience-readiness"]',
        "academy_marketing_telegram_connection_status",
      ),
    },
    {
      id: "birthday-conditions",
      title: "academy_marketing_birthday_conditions_title",
      body: "academy_marketing_birthday_conditions_body",
      prepare: scenarioStep("birthday", 3),
      interaction: informativeStep(
        '[data-academy="marketing-coupon-settings"]',
        "academy_marketing_birthday_conditions_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-wizard-conditions"]',
            '[data-academy="marketing-coupon-settings"]',
            '[data-academy="marketing-birthday-activation"]',
          ],
        },
      ),
    },
    {
      id: "test-delivery",
      title: "academy_marketing_test_title",
      body: "academy_marketing_birthday_test_body",
      prepare: scenarioStep("birthday", 3),
      interaction: informativeStep(
        '[data-academy="marketing-test-delivery"]',
        "academy_marketing_test_status",
      ),
    },
    {
      id: "birthday-review",
      title: "academy_marketing_review_title",
      body: "academy_marketing_birthday_review_body",
      prepare: scenarioStep("birthday", 4),
      interaction: informativeStep(
        '[data-academy="marketing-wizard-review"]',
        "academy_marketing_review_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-wizard-review"]',
            '[data-academy="marketing-review-edit-actions"]',
            '[data-academy="marketing-wizard-create"]',
          ],
        },
      ),
    },
    {
      id: "birthday-create",
      title: "academy_marketing_birthday_create_title",
      body: "academy_marketing_birthday_create_body",
      prepare: scenarioStep("birthday", 4),
      targetHint: "academy_marketing_create_hint",
      interaction: informativeStep(
        '[data-academy="marketing-wizard-create"]',
        "academy_marketing_create_status",
        {
          highlightSelectors: [
            '[data-academy="marketing-wizard-create"]',
            '[data-academy="marketing-wizard-cancel"]',
          ],
        },
      ),
    },
    {
      id: "complete",
      title: "academy_marketing_birthday_complete_title",
      body: "academy_marketing_birthday_complete_body",
      checklist: [
        "academy_marketing_birthday_complete_check_1",
        "academy_marketing_birthday_complete_check_2",
        "academy_marketing_birthday_complete_check_3",
        "academy_marketing_birthday_complete_check_4",
      ],
    },
  ],
};

export const MARKETING_WORKFLOWS = {
  [MARKETING_GLOBAL_WORKFLOW.key]: MARKETING_GLOBAL_WORKFLOW,
  [MARKETING_BIRTHDAY_WORKFLOW.key]: MARKETING_BIRTHDAY_WORKFLOW,
};
